import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";

const emptyItem = { drug_id: "", quantity_ordered: "", unit_cost: "" };

export default function AddPurchaseOrder({ onClose }) {
  const [vendors, setVendors] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [form, setForm] = useState({ vendor_id: "", order_date: new Date().toISOString().slice(0, 10), expected_date: "", notes: "" });
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axiosInstance.get("getVendors"),
      axiosInstance.get("getDrugs"),
    ]).then(([vRes, dRes]) => {
      setVendors(vRes.data.data || []);
      setDrugs(dRes.data.data || []);
    }).catch(() => Swal.fire("Error", "Failed to load data", "error"));
  }, []);

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleItemChange = (index, e) => {
    const updated = [...items];
    updated[index][e.target.name] = e.target.value;
    setItems(updated);
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => {
    return sum + (parseFloat(item.unit_cost || 0) * parseInt(item.quantity_ordered || 0));
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) return Swal.fire("Validation", "Please select a vendor", "warning");
    const validItems = items.filter(i => i.drug_id && i.quantity_ordered && i.unit_cost);
    if (!validItems.length) return Swal.fire("Validation", "Add at least one complete line item", "warning");

    setSaving(true);
    try {
      await axiosInstance.post("addPurchaseOrder", { ...form, items: validItems });
      Swal.fire("Success", "Purchase order created", "success");
      onClose();
    } catch {
      Swal.fire("Error", "Failed to create purchase order", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header bg-white p-3 d-flex align-items-center">
        <button className="btn btn-sm btn-light me-3" onClick={onClose}>
          <i className="ti ti-arrow-left" /> Back
        </button>
        <h5 className="mb-0">New Purchase Order</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3 mb-4">
            <div className="col-md-4">
              <label className="form-label">Vendor <span className="text-danger">*</span></label>
              <select name="vendor_id" value={form.vendor_id} onChange={handleFormChange} required className="form-control">
                <option value="">-- Select Vendor --</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Order Date <span className="text-danger">*</span></label>
              <input type="date" name="order_date" value={form.order_date} onChange={handleFormChange} required className="form-control" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Expected Delivery Date</label>
              <input type="date" name="expected_date" value={form.expected_date} onChange={handleFormChange} className="form-control" />
            </div>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleFormChange} rows={2} className="form-control" />
            </div>
          </div>

          <h6 className="mb-3">Order Items</h6>
          <div className="table-responsive mb-3">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Drug</th>
                  <th style={{ width: 120 }}>Qty Ordered</th>
                  <th style={{ width: 140 }}>Unit Cost</th>
                  <th style={{ width: 140 }}>Total</th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <select name="drug_id" value={item.drug_id} onChange={(e) => handleItemChange(i, e)} className="form-control form-control-sm">
                        <option value="">-- Select Drug --</option>
                        {drugs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="number" name="quantity_ordered" value={item.quantity_ordered} onChange={(e) => handleItemChange(i, e)} min="1" className="form-control form-control-sm" />
                    </td>
                    <td>
                      <input type="number" step="0.001" name="unit_cost" value={item.unit_cost} onChange={(e) => handleItemChange(i, e)} min="0" className="form-control form-control-sm" />
                    </td>
                    <td className="align-middle">
                      {(parseFloat(item.unit_cost || 0) * parseInt(item.quantity_ordered || 0)).toFixed(3)}
                    </td>
                    <td className="align-middle text-center">
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(i)}>
                        <i className="ti ti-x" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="text-end fw-bold">Total Amount:</td>
                  <td className="fw-bold">{totalAmount.toFixed(3)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          <button type="button" className="btn btn-outline-secondary btn-sm mb-4" onClick={addItem}>
            <i className="ti ti-plus me-1" /> Add Item
          </button>

          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="cancelText" onClick={onClose}>Cancel</button>
            <button type="submit" className="bgBtn" disabled={saving}>{saving ? "Saving..." : "Create PO"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
